/**
 * Parsers for the readable sensor characteristics.
 *
 * The device emits comma-separated `key=value` blocks terminated with CRLF and
 * padded with NULs. Two wrinkles the naive split gets wrong:
 *  - `api_key` values contain `:` (fine for a first-`=` split, but not for a
 *    `:`-split).
 *  - `onine_dev` is the LAST field and its value itself contains commas
 *    (`[0]-FF,[1]-1,...`). We therefore peel `onine_dev` off first, then parse
 *    the remaining head as a normal comma list.
 */
import type {
	SensorIdentityModel,
	SensorModuleAddressModel,
	SensorSettingsModel,
	SensorStatusModel,
} from './types';

const ONINE_DEV_KEY = 'onine_dev';

/** Strip trailing NULs / CR / LF from a raw characteristic value. */
function cleanBlock(raw: string): string {
	return raw
		.replace(/\0+$/, '')
		.replace(/[\r\n]+$/, '')
		.trim();
}

/**
 * Parse a comma-separated `key=value` list into a map. Splits each pair on its
 * FIRST `=` so values may contain `=` or `:`. Do not use on a value that may
 * contain a comma (see `onine_dev` handling in {@link parseSensorSettings}).
 */
export function parseKeyValueList(text: string): Map<string, string> {
	const map = new Map<string, string>();
	for (const pair of text.split(',')) {
		const eq = pair.indexOf('=');
		if (eq > 0) {
			map.set(pair.slice(0, eq).trim(), pair.slice(eq + 1));
		}
	}
	return map;
}

function numberOrNull(value: string | undefined): number | null {
	if (value == null || value.trim() === '') {
		return null;
	}
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

function stringOrNull(value: string | undefined): string | null {
	return value == null || value === '' ? null : value;
}

/**
 * Parse the `onine_dev` value (`[0]-FF,[1]-1,[2]-2,...`) into module addresses.
 * Malformed entries are skipped rather than throwing.
 */
export function parseModuleAddresses(
	onineDev: string,
): Array<SensorModuleAddressModel> {
	const out: Array<SensorModuleAddressModel> = [];
	for (const entry of onineDev.split(',')) {
		const match = /^\s*\[(\d+)\]-(.+?)\s*$/.exec(entry);
		if (match != null) {
			out.push({ index: Number(match[1]), address: match[2] ?? '' });
		}
	}
	return out;
}

/** Parse the device settings block (`fff00105`). */
export function parseSensorSettings(raw: string): SensorSettingsModel {
	const clean = cleanBlock(raw);

	// Peel off onine_dev (last field, comma-containing value) before splitting.
	let head = clean;
	let onineDev = '';
	const marker = `,${ONINE_DEV_KEY}=`;
	const at = clean.indexOf(marker);
	if (at >= 0) {
		head = clean.slice(0, at);
		onineDev = clean.slice(at + marker.length);
	} else if (clean.startsWith(`${ONINE_DEV_KEY}=`)) {
		head = '';
		onineDev = clean.slice(`${ONINE_DEV_KEY}=`.length);
	}

	const map = parseKeyValueList(head);
	return {
		serverUrl: stringOrNull(map.get('url')),
		apiKey: stringOrNull(map.get('api_key')),
		apnName: stringOrNull(map.get('apn_name')),
		moduleCount: numberOrNull(map.get('LD_n')),
		reportInterval: numberOrNull(map.get('upp')),
		addrEncodeMode: numberOrNull(map.get('addr_encode_mode')),
		moduleAddresses: onineDev === '' ? [] : parseModuleAddresses(onineDev),
		raw: clean,
	};
}

/** Parse the device identity block (`fff00100`): `mfn,dn,sn,hwv,fwv`. */
export function parseSensorIdentity(raw: string): SensorIdentityModel {
	const clean = cleanBlock(raw);
	const map = parseKeyValueList(clean);
	return {
		manufacturer: stringOrNull(map.get('mfn')),
		deviceName: stringOrNull(map.get('dn')),
		serialNumber: stringOrNull(map.get('sn')),
		hardwareVersion: stringOrNull(map.get('hwv')),
		firmwareVersion: stringOrNull(map.get('fwv')),
		raw: clean,
	};
}

/**
 * Parse the cell/battery status block (`fff00101`).
 *
 * Field names come from the block itself; the semantics are the vendor UI's
 * own labels (BX_BLE: 充电状态 charging, 网络状态 network registration,
 * 信号强度 signal strength, 信号质量 signal quality, 电池电压 battery
 * voltage, 网络制式 network type, 参考接收功率 reference received power).
 *
 * This is the block that answers "is the unit even on the cellular network?"
 * — a fully configured unit that never reports is usually unregistered.
 */
export function parseSensorStatus(raw: string): SensorStatusModel {
	const clean = cleanBlock(raw);
	const map = parseKeyValueList(clean);
	return {
		powerSource: numberOrNull(map.get('power')),
		charging: numberOrNull(map.get('chg')),
		batteryMillivolts: numberOrNull(map.get('vbat')),
		temperatureC: numberOrNull(map.get('tmpr')),
		networkType: stringOrNull(map.get('net')),
		registered: numberOrNull(map.get('reg')),
		signalQuality: numberOrNull(map.get('csq')),
		signalStrength: numberOrNull(map.get('ss')),
		bitErrorRate: numberOrNull(map.get('ber')),
		referenceSignalQuality: numberOrNull(map.get('rsrq')),
		referenceSignalPower: numberOrNull(map.get('rsrp')),
		raw: clean,
	};
}

/**
 * Human meaning of the cellular registration code (`reg`), which follows the
 * standard modem network-registration values. The distinction that matters
 * on-site is DENIED vs SEARCHING vs REGISTERED: a unit with good signal that
 * is denied has a SIM/carrier problem, not a coverage or config problem.
 */
export function describeRegistrationCode(code: number | null): string {
	switch (code) {
		case 0:
			return 'Not registered — not searching';
		case 1:
			return 'Registered (home network)';
		case 2:
			return 'Searching for a network';
		case 3:
			return 'Registration DENIED by the carrier — check the SIM';
		case 4:
			return 'Unknown state';
		case 5:
			return 'Registered (roaming)';
		default:
			return code == null ? 'Not reported' : `Unrecognized code ${code}`;
	}
}

/** Whether the registration code means the unit can reach the server. */
export function isRegistered(code: number | null): boolean {
	return code === 1 || code === 5;
}
